/**
 * Command Center score helpers (blueprint §5.2 cc, §7 C4/C6/C7/C9). Pure, so
 * the rules are unit-tested apart from React.
 *
 * Known-value rule (blueprint "§5.2 wiring note"): a component the tenant has
 * no data for is `null`, never a fabricated 0. Dashboard stats report a 0%
 * baseline when nothing was checked (`totalChecks === 0`, also the error
 * fallback), and user insights report 0% adoption when `hasData` is false.
 * Both read as "no data" here, so the Guardian Score, the waffle and the
 * pillar tiles agree.
 */
import { largestRemainder, type Category, type WaffleSegment } from '@/components/carbon'
import type { DashboardStats, UserInsightsSummary } from '@/types'
import type { ScoreDataPoint, ScoreWithTrend } from '@/types/executiveReport'
import { formatAssessedClock } from '@/components/common/LastAssessed'

/** The four Guardian Score components, 0..100 each, or null when unknown. */
export interface GuardianComponents {
  baseline: number | null
  bpa: number | null
  identity: number | null
  secure: number | null
}

/** Each component's share of the 100 points (`calculateUnifiedGuardianScore`). */
export const GUARDIAN_WEIGHTS: Record<Category, number> = {
  baseline: 20,
  bpa: 30,
  identity: 20,
  secure: 30,
}

/** Waffle key labels, in waffle order (DESIGN.md "Guardian Score waffle"). */
export const COMPONENT_LABEL: Record<Category, string> = {
  baseline: 'Baseline',
  bpa: 'Best Practice',
  identity: 'Identity',
  secure: 'Secure Score',
}

const ORDER: Category[] = ['baseline', 'bpa', 'identity', 'secure']

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** Baseline compliance %, or null when no resource was checked (nothing scanned, or the stats call failed). */
export function knownBaselinePct(stats: DashboardStats | null | undefined): number | null {
  const score = stats?.complianceScore
  if (!score || !(score.totalChecks > 0)) return null
  return finite(score.percentage)
}

/**
 * Member MFA adoption %, or null when the summary is missing, has no data yet,
 * or Exchange mailbox classification is still refining (shared and resource
 * mailboxes count as members until it lands, deflating adoption). Strict
 * `=== false` keeps older summaries (undefined) ungated, as PillarIdentity does.
 */
export function knownMfaPct(insights: UserInsightsSummary | null | undefined): number | null {
  if (!insights || !insights.hasData) return null
  if (insights.mailboxClassificationAvailable === false) return null
  return finite(insights.mfaMemberAdoptionPercentage)
}

export function guardianComponents(input: {
  stats: DashboardStats | null | undefined
  insights: UserInsightsSummary | null | undefined
  bpa: number | null | undefined
  secure: number | null | undefined
}): GuardianComponents {
  return {
    baseline: knownBaselinePct(input.stats),
    bpa: finite(input.bpa),
    identity: knownMfaPct(input.insights),
    secure: finite(input.secure),
  }
}

/**
 * The waffle's four groups (§7 C7). Each known component earns
 * `value × weight / 100` points; largestRemainder rounds them so the squares
 * add up to the rounded total, which is the Guardian Score whenever all four
 * components are known. A missing component stays an empty group ("No data").
 */
export function waffleSegments(c: GuardianComponents): WaffleSegment[] {
  const raw = ORDER.map((k) => {
    const v = c[k]
    return v == null ? 0 : (Math.max(0, Math.min(100, v)) * GUARDIAN_WEIGHTS[k]) / 100
  })
  const total = Math.round(raw.reduce((a, b) => a + b, 0))
  const earned = largestRemainder(raw, total)
  return ORDER.map((k, i) => ({
    category: k,
    earned: c[k] == null ? null : earned[i],
    total: GUARDIAN_WEIGHTS[k],
    label: COMPONENT_LABEL[k],
  }))
}

/* -------------------------------------------------------------------------- */
/* Deltas                                                                     */
/* -------------------------------------------------------------------------- */

/** "+7", "−2.5", "0": one decimal at most, a true minus sign. */
export function signed(n: number): string {
  const r = Math.round(n * 10) / 10
  const abs = Number.isInteger(r) ? String(Math.abs(r)) : Math.abs(r).toFixed(1)
  if (r === 0) return '0'
  return `${r > 0 ? '+' : '−'}${abs}`
}

/** "Oct 2" from an ISO date or date-time, read as a calendar day (no time-zone shift). */
export function dayLabel(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * The Guardian Score tile's delta tag (§7 C6): the trend's last score against
 * the point seven entries earlier, "+1 since Sep 25". Hidden (null) with
 * fewer than 8 daily points.
 */
export function scoreDeltaSince(trend: ReadonlyArray<ScoreDataPoint> | null | undefined): string | null {
  const pts = (trend ?? [])
    .filter((p) => finite(p.guardianScore) != null && dayLabel(p.date) != null)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
  if (pts.length < 8) return null
  const last = pts[pts.length - 1]
  const base = pts[pts.length - 8]
  const delta = (last.guardianScore as number) - (base.guardianScore as number)
  const since = dayLabel(base.date)
  if (Math.round(delta * 10) / 10 === 0) return `No change since ${since}`
  return `${signed(delta)} since ${since}`
}

/**
 * A delta tag from an executive report's ScoreWithTrend, "+7 in 90 days", or null when unknown. The Command
 * Center no longer reads the report; its tiles use windowDelta (cc-history.ts).
 */
export function periodDelta(s: ScoreWithTrend | null | undefined, days: number | null | undefined): string | null {
  const change = finite(s?.change)
  const span = finite(days)
  if (change == null || span == null || span <= 0) return null
  if (Math.round(change * 10) / 10 === 0) return `No change in ${span} days`
  return `${signed(change)} in ${span} days`
}

/* -------------------------------------------------------------------------- */
/* Provenance                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The brief's provenance line (DESIGN.md, §7 C4): "Assessed by Guardian at
 * 06:12 today · <tenant name>". The mockup's "8 workloads" clause is
 * dropped: no loaded source counts the workloads Guardian assessed (the
 * client-side BPA workload summary counts workloads with violations only).
 */
export function commandCenterProvenance(input: {
  at: string | null | undefined
  tenantName?: string | null
  now?: Date
}): string {
  const clock = input.at ? formatAssessedClock(input.at, input.now) : null
  const lead = clock ? `Assessed by Guardian ${clock}` : input.at ? 'Assessed by Guardian' : 'Awaiting first assessment'
  return [lead, input.tenantName?.trim() || null].filter(Boolean).join(' · ')
}
